/**
 * Client-side pre-checks for document uploads. The server is authoritative
 * (it re-validates type/size and computes the SHA-256); these only give the
 * user an immediate, readable answer before a 25 MB upload starts.
 */
export const ALLOWED_DOCUMENT_EXTENSIONS = [
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'docx', 'xlsx', 'pptx', 'zip', 'txt', 'csv',
];
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const DOCUMENT_ACCEPT = ALLOWED_DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(',');

export type FileProblem = 'type' | 'size' | null;

export function checkDocumentFile(file: File): FileProblem {
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(ext)) return 'type';
  if (file.size > MAX_DOCUMENT_BYTES) return 'size';
  return null;
}

export function formatBytes(bytes: number | null | undefined): string {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Q-DQ-295: the types a browser can show in its own tab (images and PDF); everything else is download-only. */
const PREVIEW_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
export function isPreviewable(fileName: string | null | undefined): boolean {
  const name = fileName ?? '';
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  return PREVIEW_EXTENSIONS.includes(ext);
}

/** File name without its extension — a sensible default document title. */
export function titleFromFileName(name: string): string {
  const i = name.lastIndexOf('.');
  return (i > 0 ? name.slice(0, i) : name).replace(/[_-]+/g, ' ').trim();
}
